'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole = 'admin' | 'broker' | 'underwriter' | 'sales_support' | 'external' | 'client' | 'contact_centre' | 'claims' | 'engineer' | 'finance' | 'claims_manager';

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  userRole: UserRole | null;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
  resetPassword: (email: string) => Promise<any>;
  updatePassword: (newPassword: string) => Promise<any>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const supabase = createClient();
  // Guard: track whether the initial session load has completed so that
  // onAuthStateChange INITIAL_SESSION event does not trigger a second
  // round of DB queries.
  const initializedRef = useRef(false);

  // Purge all Supabase auth tokens from cookies and localStorage
  const purgeAuthTokens = () => {
    if (typeof document !== 'undefined') {
      document.cookie.split(';').forEach((c) => {
        const name = c.trim().split('=')[0];
        if (name.includes('sb-') || name.includes('supabase')) {
          document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
        }
      });
    }
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('sb_') || k.includes('supabase') || k.includes('sb-')) {
          localStorage.removeItem(k);
        }
      });
    } catch {}
  };

  const clearAuthState = () => {
    purgeAuthTokens();
    setSession(null);
    setUser(null);
    setUserRole(null);
    setLoading(false);
  };

  const isInvalidJwtError = (error: any): boolean => {
    const msg = error?.message ?? '';
    return (
      msg.includes('InvalidJWTToken') ||
      msg.includes('Invalid value for JWT claim') ||
      msg.includes('invalid JWT') ||
      msg.includes('JWT expired') ||
      msg.includes('invalid claim')
    );
  };

  const fetchUserRole = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .single();
      if (data?.role) {
        setUserRole(data.role as UserRole);
        try {
          sessionStorage.setItem('activenet_user_role', data.role);
        } catch {}
        return data.role as UserRole;
      }
    } catch {
      // Role fetch failed — fall back to metadata
    }
    return null;
  };

  useEffect(() => {
    // ── Initial session load ─────────────────────────────────────────────
    // Use getSession() first — this reads from the local cookie/storage and
    // does NOT make a network request to Supabase if a valid token is cached.
    // Only call getUser() (which validates server-side) when we have a session,
    // to avoid an unnecessary network round-trip on every page load for
    // unauthenticated visitors.
    supabase.auth.getSession().then(async ({ data: { session: cachedSession }, error: sessionError }) => {
      if (sessionError || !cachedSession) {
        // No session cached — user is not logged in
        setSession(null);
        setUser(null);
        setUserRole(null);
        setLoading(false);
        initializedRef.current = true;
        return;
      }

      // We have a cached session — validate it server-side with a single getUser() call
      const { data: { user: validatedUser }, error: userError } = await supabase.auth.getUser();

      if (userError || !validatedUser) {
        if (userError && isInvalidJwtError(userError)) {
          clearAuthState();
          initializedRef.current = true;
          return;
        }
        setSession(null);
        setUser(null);
        setUserRole(null);
        setLoading(false);
        initializedRef.current = true;
        return;
      }

      // Valid session — set state
      setSession(cachedSession);
      setUser(validatedUser);

      // Fetch DB role (single DB call)
      const dbRole = await fetchUserRole(validatedUser.id);
      if (!dbRole) {
        const metaRole = validatedUser.user_metadata?.role as UserRole | undefined;
        if (metaRole) setUserRole(metaRole);
      }
      setLoading(false);
      initializedRef.current = true;
    });

    // ── Auth state change listener ───────────────────────────────────────
    // This fires for SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, etc.
    // We skip the INITIAL_SESSION event because we handle it above via
    // getSession() to avoid a duplicate round of Supabase calls.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Skip the initial session event — already handled above
      if (_event === 'INITIAL_SESSION') {
        return;
      }

      // Skip SIGNED_IN events when the login page is managing its own auth flow.
      // The login page calls signInWithPassword directly and handles MFA + redirect
      // itself. Allowing AuthContext to react to SIGNED_IN here causes a full
      // re-render of the app tree mid-flow, which resets the preview and breaks login.
      if (_event === 'SIGNED_IN') {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        if (currentPath === '/login' || currentPath === '/activenet/login') {
          return;
        }
      }

      // On token refresh failure, clear state so the login page is shown
      if (_event === 'TOKEN_REFRESHED' && !session) {
        clearAuthState();
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Fetch DB role on sign-in / token refresh
        fetchUserRole(session.user.id).then((dbRole) => {
          if (!dbRole) {
            const metaRole = session.user?.user_metadata?.role as UserRole | undefined;
            if (metaRole) setUserRole(metaRole);
          }
          setLoading(false);
        });
      } else {
        setUserRole(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://underwrite7354.builtwithrocket.new';
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'broker',
        },
        emailRedirectTo: `${origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserRole(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Send password reset email
  const resetPassword = async (email: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://underwrite7354.builtwithrocket.new';
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/reset-password`,
    });
    if (error) throw error;
    return data;
  };

  // Update password (after reset link clicked)
  const updatePassword = async (newPassword: string) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return data;
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    userRole,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
